/**
 * Exact fractions for probability trees, so 0.3 + 0.7 is exactly 1 and 5/8 × 4/7 is
 * 5/14 rather than 0.35714…
 *
 * Plain numbers with a numerator and denominator kept as safe integers. An operation
 * that would leave the safe-integer range returns null instead of a wrong answer, and
 * the caller falls back to decimals — which only ever happens for a tree with
 * long decimal probabilities many levels deep.
 */

export interface Rational {
  n: number;
  d: number;
}

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    [x, y] = [y, x % y];
  }
  return x;
}

function make(n: number, d: number): Rational | null {
  if (!(Number.isSafeInteger(n) && Number.isSafeInteger(d)) || d === 0) {
    return null;
  }
  const divisor = gcd(n, d) || 1;
  const sign = d < 0 ? -1 : 1;
  return { n: (sign * n) / divisor, d: (sign * d) / divisor };
}

export const ZERO: Rational = { n: 0, d: 1 };
export const ONE: Rational = { n: 1, d: 1 };

export function fraction(n: number, d: number): Rational | null {
  return make(n, d);
}

export function ratAdd(a: Rational, b: Rational): Rational | null {
  return make(a.n * b.d + b.n * a.d, a.d * b.d);
}

export function ratSub(a: Rational, b: Rational): Rational | null {
  return make(a.n * b.d - b.n * a.d, a.d * b.d);
}

export function ratMul(a: Rational, b: Rational): Rational | null {
  return make(a.n * b.n, a.d * b.d);
}

export function ratValue(r: Rational): number {
  return r.n / r.d;
}

export function ratEquals(a: Rational, b: Rational): boolean {
  return a.n === b.n && a.d === b.d;
}

const DECIMAL = /^[+]?(\d*)\.?(\d*)$/;
const FRACTION = /^(\d+)\s*\/\s*(\d+)$/;
const MAX_DECIMAL_PLACES = 9;

/**
 * A probability typed as 0.3, .3, 30%, 3/10 or 1. Returns null for anything else, for
 * a zero denominator, or for a value outside 0–1.
 */
export function parseProbability(raw: string): Rational | null {
  const text = raw.trim().replace(",", ".");
  const parsed = parseAny(text);
  if (parsed === null || parsed.n < 0 || parsed.n > parsed.d) {
    return null;
  }
  return parsed;
}

function parseAny(text: string): Rational | null {
  const fractionMatch = FRACTION.exec(text);
  if (fractionMatch) {
    return make(Number(fractionMatch[1]), Number(fractionMatch[2]));
  }
  const percent = text.endsWith("%");
  const body = percent ? text.slice(0, -1).trim() : text;
  const decimal = DECIMAL.exec(body);
  if (!decimal || (decimal[1] === "" && decimal[2] === "")) {
    return null;
  }
  const places = decimal[2].length;
  if (places > MAX_DECIMAL_PLACES) {
    return null;
  }
  return make(
    Number(`${decimal[1]}${decimal[2]}`),
    10 ** places * (percent ? 100 : 1)
  );
}

/** "3/10", or "1" and "0" for whole values. */
export function fractionText(r: Rational): string {
  return r.d === 1 ? String(r.n) : `${r.n}/${r.d}`;
}
